import { AlertModal } from '@/components/AlertModal';
import Loader from '@/components/Loader';
import { VideoModal } from '@/components/VideoModal';
import { useHasScopes } from '@/hooks/useHasScopes';
import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { cn } from '@/utils/utils';
import type { LedPanelResizedDto } from '@shelflix/trpc/routes/media/ledPanelResized/useCase';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const ALL_BRANDS = 'all';

const STATUS_LABEL: Record<string, string> = {
  ready: 'Pronto',
  processing: 'Processando…',
  pending: 'Na fila',
  failed: 'Falhou',
};
const STATUS_CLASS: Record<string, string> = {
  ready: 'bg-emerald-500/15 text-emerald-400',
  processing: 'bg-sky-500/15 text-sky-400',
  pending: 'bg-amber-500/15 text-amber-400',
  failed: 'bg-red-500/15 text-red-400',
};

function formatGeometry({ geometry }: LedPanelResizedDto) {
  return `${geometry.widthPx}×${geometry.heightPx} em ${geometry.offsetXPx},${geometry.offsetYPx} · saída ${geometry.outputResolution.replace('x', '×')}`;
}

function displayStatus(item: LedPanelResizedDto): { label: string; className: string } {
  const muted = 'bg-zinc-500/15 text-zinc-400';
  // Only the campaign playing on a panel right now gets converted
  if (item.status === 'pending' && !item.inUse) return { label: 'Não será gerado', className: muted };
  if (item.waitingCampaign) return { label: 'Aguarda início da campanha', className: muted };
  return {
    label: STATUS_LABEL[item.status] ?? item.status,
    className: STATUS_CLASS[item.status] ?? STATUS_CLASS.pending,
  };
}

/** Reads the real dimensions of a local video file, to refuse a replacement of the wrong size */
function readVideoSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({ width: video.videoWidth, height: video.videoHeight });
      URL.revokeObjectURL(url);
    };
    video.onerror = () => {
      reject(new Error('Não foi possível ler o vídeo. Use um arquivo .mp4 (H.264).'));
      URL.revokeObjectURL(url);
    };
    video.src = url;
  });
}

/**
 * Mídias -> "Redimensionado": the versions of videos sized/positioned for Painel LED materials.
 * They are generated automatically; here they can be watched, deleted (generated again only if
 * a panel still needs it) or replaced by a file the user prepared.
 */
export function LedPanelResizedList() {
  const queryClient = useQueryClient();
  const canEdit = useHasScopes(['media:edit']);
  const canDelete = useHasScopes(['media:delete']);

  const [search, setSearch] = useState('');
  const [brandUuid, setBrandUuid] = useState(ALL_BRANDS);
  const [toDelete, setToDelete] = useState<LedPanelResizedDto | null>(null);
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceTargetRef = useRef<LedPanelResizedDto | null>(null);

  const listQuery = trpc.media.ledPanelResizedList.queryOptions({});
  const { data, isLoading, isError } = useQuery({
    ...listQuery,
    refetchInterval: (query) =>
      query.state.data?.some((v) => (v.status === 'pending' && !v.waitingCampaign) || v.status === 'processing') ? 15_000 : false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: listQuery.queryKey });

  const deleteMutation = useMutation(trpc.media.ledPanelResizedDelete.mutationOptions());
  const replaceStartMutation = useMutation(trpc.media.ledPanelResizedReplaceStart.mutationOptions());
  const replaceConfirmMutation = useMutation(trpc.media.ledPanelResizedReplaceConfirm.mutationOptions());

  const items = data ?? [];
  const brands = useMemo(() => {
    const byUuid = new Map(items.map((v) => [v.brand.uuid, v.brand.name]));
    return Array.from(byUuid.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [items]);
  const visible = items.filter(
    (v) =>
      (brandUuid === ALL_BRANDS || v.brand.uuid === brandUuid) &&
      (!search.trim() ||
        `${v.mediaName} ${v.fileName ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()))
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(
      { variantId: toDelete.variantId },
      {
        onSuccess: () => {
          toast.success('Vídeo redimensionado excluído');
          setToDelete(null);
          refresh();
        },
        onError: (error) => toast.error(`Erro ao excluir: ${error.message}`),
      }
    );
  };

  const pickReplacement = (item: LedPanelResizedDto) => {
    replaceTargetRef.current = item;
    fileInputRef.current?.click();
  };

  const uploadReplacement = async (file: File) => {
    const item = replaceTargetRef.current;
    if (!item) return;
    setReplacingId(item.variantId);
    try {
      if (file.type !== 'video/mp4') {
        throw new Error('O arquivo precisa ser .mp4');
      }
      // The file is played full screen on the TV box HDMI output - with any other size the
      // panel rectangle would no longer land on the configured position
      const [outWidth, outHeight] = item.geometry.outputResolution.split('x').map(Number);
      const { width, height } = await readVideoSize(file);
      if (width !== outWidth || height !== outHeight) {
        throw new Error(
          `O vídeo tem ${width}×${height}, mas precisa ter ${outWidth}×${outHeight} (a saída da TV box), com o conteúdo do painel em ${item.geometry.offsetXPx},${item.geometry.offsetYPx} ocupando ${item.geometry.widthPx}×${item.geometry.heightPx}.`
        );
      }

      const { storageKey, upload } = await replaceStartMutation.mutateAsync({ variantId: item.variantId });
      const formData = new FormData();
      Object.entries(upload.fields ?? {}).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);
      const response = await fetch(upload.url, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`Falha no envio do arquivo (HTTP ${response.status})`);

      await replaceConfirmMutation.mutateAsync({ variantId: item.variantId, storageKey });
      toast.success('Vídeo substituído', { description: 'Os painéis vão receber o novo arquivo.' });
      refresh();
    } catch (error) {
      toast.error('Não foi possível substituir o vídeo', {
        description: error instanceof Error ? error.message : String(error),
        duration: 10_000,
      });
    } finally {
      setReplacingId(null);
      replaceTargetRef.current = null;
    }
  };

  return (
    <BaseLayout>
      <section className="flex flex-col gap-6">
        <p className="text-sm text-[var(--gray-primary)]">
          Versões dos vídeos já no tamanho e na posição de cada Painel LED. São geradas automaticamente para a campanha
          que está passando no painel; as campanhas continuam usando o vídeo original.
        </p>

        <div className="bg-card flex flex-col gap-4 rounded-[10px] p-4 md:flex-row">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-xs font-medium text-[var(--gray-primary)]">Nome</label>
            <Input
              className="h-[36px]"
              placeholder="Buscar pelo nome do vídeo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-xs font-medium text-[var(--gray-primary)]">Marca</label>
            <Select value={brandUuid} onValueChange={setBrandUuid}>
              <SelectTrigger className="h-[36px] w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_BRANDS}>Todas as marcas</SelectItem>
                {brands.map(([uuid, name]) => (
                  <SelectItem key={uuid} value={uuid}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading && <Loader message="Carregando vídeos redimensionados..." />}
        {isError && <p className="p-4 text-red-500">Erro ao carregar os vídeos redimensionados.</p>}
        {!isLoading && !isError && !visible.length && (
          <p className="p-4">Nenhum vídeo redimensionado{items.length ? ' para este filtro' : ''}.</p>
        )}

        {!isLoading && !isError && !!visible.length && (
          <ul className="divide-y divide-[var(--border)] rounded-[10px] border border-[var(--border)]">
            {visible.map((item) => (
              <li key={item.variantId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <span className="material-symbols-outlined text-[var(--gray-primary)]">movie</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.fileName ?? `${item.mediaName} (ainda não gerado)`}</p>
                  <p className="truncate text-xs text-[var(--gray-primary)]">
                    Original: {item.mediaName} · {item.brand.name}
                  </p>
                  <p className="truncate text-xs text-[var(--gray-primary)]">{formatGeometry(item)}</p>
                  {item.status === 'failed' && item.errorMessage && (
                    <p className="text-xs break-words text-red-400">{item.errorMessage}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {item.isCustom && (
                    <span className="rounded-full bg-violet-500/15 px-2.5 py-0.5 text-xs font-medium text-violet-400">
                      Personalizado
                    </span>
                  )}
                  {!item.inUse && (
                    <span
                      className="rounded-full bg-zinc-500/15 px-2.5 py-0.5 text-xs font-medium text-zinc-400"
                      title="Nenhum painel ativo usa mais este tamanho/posição"
                    >
                      Sem painel usando
                    </span>
                  )}
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-medium',
                      displayStatus(item).className
                    )}
                  >
                    {displayStatus(item).label}
                  </span>
                  {item.previewUrl && (
                    <VideoModal
                      videos={[item.previewUrl]}
                      trigger={
                        <Button type="button" variant="secondary" size="sm">
                          Ver
                        </Button>
                      }
                    />
                  )}
                  {canEdit && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={replacingId === item.variantId}
                      disabled={replacingId !== null}
                      onClick={() => pickReplacement(item)}
                    >
                      Substituir
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="text-red-400"
                      onClick={() => setToDelete(item)}
                    >
                      Excluir
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void uploadReplacement(file);
          }}
        />

        <AlertModal
          openModal={!!toDelete}
          onOpenChange={(open) => !open && setToDelete(null)}
          icon={<span className="material-symbols-outlined text-3xl text-red-400">delete</span>}
          title="Excluir vídeo redimensionado?"
          subtitle={
            toDelete?.inUse
              ? 'Os painéis que usam este vídeo voltam a tocar o original e, se ele estiver passando agora, será gerado de novo automaticamente.'
              : 'Nenhum painel ativo usa este tamanho/posição. O arquivo será apagado.'
          }
          content={<p className="mb-4 text-center text-sm break-all">{toDelete?.fileName ?? toDelete?.mediaName}</p>}
          actionBotton={
            <Button
              type="button"
              className="w-full bg-red-500 hover:bg-red-600"
              loading={deleteMutation.isPending}
              onClick={confirmDelete}
            >
              Excluir
            </Button>
          }
        />
      </section>
    </BaseLayout>
  );
}
